/// <amd-module name="Feed/Legacy/Components/Settings/FeedDetailsPane" />
import * as React from "react";
import { connect } from "react-redux";

import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { ITextField, TextField } from "OfficeFabric/TextField";

import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";

import { Checkbox } from "VSSUI/Checkbox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { FwLinks, SettingsPivotKeys } from "Feed/Common/Constants/Constants";
import * as PackageResources from "Feed/Common/Resources";
import { IFeedHubViewAwareState, isSelectedFeedDeleted } from "Feed/Core/Redux/FeedHub";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { IRetentionPolicySettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { FeedRetentionChanges } from "Feed/Legacy/Types/FeedRetentionChanges";

export interface IFeedDetailsPaneProps extends IRetentionPolicySettingsState {
    pageContext: IVssPageContext;
    /**
     * feed name to display
     */
    feedName: string;

    /**
     * feed description to display
     */
    feedDescription: string;

    /**
     * hideDeletedPackageVersions value to display
     */
    hideDeletedPackageVersions: boolean;

    /**
     * badgesEnabled value to display
     */
    badgesEnabled: boolean;

    /**
     * error message to display for feed name
     */
    feedNameErrorMessage: string;

    /**
     * True if the current user can make changes to the feed settings
     */
    isUserAdmin: boolean;

    /**
     * True if user has made changes to feed name/description/hideDeletedPackageVersions
     */
    hasChanges: () => boolean;

    /**
     * True if there are validation errors in the pivot
     */
    hasValidationErrors: () => boolean;

    /**
     * True if user has clicked save and save operation is not done yet
     */
    isSavingChanges: boolean;

    retentionPoliciesActive: boolean;
    retentionPolicyCountLimit: string;
    retentionPolicyDaysLimit: string;
    retentionPolicyCountErrorMessage: string;
    retentionPolicyDaysToKeepErrorMessage: string;
    retentionPolicyMinCountLimit: number;
    retentionPolicyMaxCountLimit: number;
    retentionPolicyMinDaysToKeepLimit: number;
    retentionPolicyMaxDaysToKeepLimit: number;
    retentionPolicyCountLimitDefault: number;
    retentionPolicyDaysToKeepLimitDefault: number;

    /**
     * Feed Upgrade Available
     */
    upgradeAvailable: boolean;

    /**
     * Feed Upgrade is in progress
     */
    upgradeInProgress: boolean;
}

export interface IFeedDetailsPaneProps2 {
    /**
     * Feed soft deleted state
     */
    isDeleted: boolean;
}

export interface IFeedDetailsPaneState {
    upgradeButtonClicked: boolean;
}

class UnconnectedFeedDetailsPane extends React.Component<
    IFeedDetailsPaneProps & IFeedDetailsPaneProps2,
    IFeedDetailsPaneState
> {
    private readonly _maxFeedNameLength = 64;
    private readonly _maxFeedDescriptionLength = 255;
    private readonly _feedDescriptionFieldRows = 5;
    private _textField: ITextField;
    private _feedRetentionChange: FeedRetentionChanges;

    constructor(props: IFeedDetailsPaneProps & IFeedDetailsPaneProps2) {
        super(props);

        this.state = {
            upgradeButtonClicked: false
        };
    }

    public componentDidUpdate() {
        // Setting focus on textfield only when upgrade button was successfully clicked,
        // because the upgrade button will disappear on click and the focus needs to be set, otherwise it gets lost.
        if (this.props.upgradeInProgress == true && this._textField && this.state.upgradeButtonClicked === true) {
            this._textField.focus();
        }
    }

    public render(): JSX.Element {
        const formHeadingId = "feed-details-form-h2";
        const manualUpgradeId = "feed-details-manual-upgrade";
        const deletedPackagesId = "feed-details-deleted-packages";
        const packageSharingId = "feed-details-package-sharing";
        const retentionPoliciesId = "feed-details-retention-policies";
        this._feedRetentionChange = {
            maximumCount: this.props.retentionPolicyCountLimit,
            daysToKeep: this.props.retentionPolicyDaysLimit,
            active:
                this.props.retentionPolicyCountLimit != null && this.props.retentionPolicyDaysLimit != null
                    ? true
                    : false
        };
        return (
            <div role="form" aria-labelledby={formHeadingId} className="feed-details-pane">
                <h2 id={formHeadingId} className="visibly-hidden">
                    {
                        /*This hidden heading is here for accessibility purposes*/
                        PackageResources.FeedDetailsPane_Heading
                    }
                </h2>
                {this.props.upgradeInProgress && (
                    <section aria-labelledby={manualUpgradeId}>
                        <h3 className={"fontSizeL header margin-bottom"}>
                            {Utils_String.format(PackageResources.FeedDetailsPane_ManualUpgrade, this.props.feedName)}
                        </h3>
                        <div className={"fontSizeM margin-bottom"}>
                            {PackageResources.FeedDetailsPane_UpgradeInProgress}
                        </div>
                    </section>
                )}
                {this.props.upgradeAvailable && !this.props.upgradeInProgress && (
                    <section aria-labelledby={manualUpgradeId}>
                        <h3 id={manualUpgradeId} className={"fontSizeL header margin-bottom"}>
                            {Utils_String.format(PackageResources.FeedDetailsPane_ManualUpgrade, this.props.feedName)}
                        </h3>
                        <div className={"fontSizeM margin-bottom"}>
                            {PackageResources.FeedDetailsPane_UpgradeMessage1}
                        </div>
                        <div className={"fontSizeM margin-bottom"}>
                            {PackageResources.FeedDetailsPane_UpgradeMessage2}
                        </div>
                        <ul className={"fontSizeM margin-bottom"}>
                            <li>{PackageResources.FeedDetailsPane_UpgradeItem1}</li>
                            <li>{PackageResources.FeedDetailsPane_UpgradeItem2}</li>
                            <li>{PackageResources.FeedDetailsPane_UpgradeItem3}</li>
                        </ul>
                        <div className={"fontSizeM margin-bottom"}>
                            <ExternalLink
                                pageContext={this.props.pageContext}
                                className={"external-link margin-bottom"}
                                href={FwLinks.ManualUpgradeLearnMore}
                            >
                                {PackageResources.FeedDetailsPane_UpgradeLearnMore}
                            </ExternalLink>
                        </div>
                        <div className={"fontSizeM margin-bottom"}>
                            <DefaultButton
                                onClick={() => this._feedDetailsUpgrade()}
                                disabled={this.props.isSavingChanges === true}
                                text={PackageResources.FeedDetailsPane_ManualUpgradeAction}
                                ariaLabel={Utils_String.format(
                                    PackageResources.FeedDetailsPane_ManualUpgrade,
                                    this.props.feedName
                                )}
                                ariaDescription={PackageResources.FeedDetailsPane_UpgradeMessage1}
                            />
                        </div>
                    </section>
                )}
                <TextField
                    className={"text-field margin-bottom"}
                    label={PackageResources.FeedDetailsPane_Name}
                    value={this.props.feedName}
                    maxLength={this._maxFeedNameLength}
                    errorMessage={this.props.feedNameErrorMessage}
                    onChanged={this._onfeedNameChanged}
                    disabled={!this.props.isUserAdmin || this.props.isDeleted}
                    componentRef={(element: ITextField) => (this._textField = element)}
                />
                <TextField
                    className={"text-field margin-bottom"}
                    label={PackageResources.FeedDetailsPane_Description}
                    value={this.props.feedDescription}
                    multiline={true}
                    maxLength={this._maxFeedDescriptionLength}
                    rows={this._feedDescriptionFieldRows}
                    resizable={false}
                    autoAdjustHeight={true}
                    onChanged={this._onfeedDescriptionChanged}
                    disabled={!this.props.isUserAdmin || this.props.isDeleted}
                />
                {!this.props.isDeleted && (
                    <section aria-labelledby={deletedPackagesId}>
                        <h3 id={deletedPackagesId} className={"fontSizeL header margin-bottom"}>
                            {PackageResources.FeedDetailsPane_DeletedPackages}
                        </h3>
                        <p className={"fontSizeM margin-bottom"}>
                            <FormatComponent format={PackageResources.FeedDetailsPane_DeletedPackagesDescription}>
                                <ExternalLink
                                    pageContext={this.props.pageContext}
                                    href={FwLinks.EditFeedDeletedPackagesTabImmutableFeed}
                                    underline={true}
                                >
                                    {PackageResources.FeedDetailsPane_FeedsAreImmutable}
                                </ExternalLink>
                            </FormatComponent>
                        </p>
                        <Checkbox
                            className="edit-checkBox"
                            checked={this.props.hideDeletedPackageVersions === true}
                            onChange={this._onhideDeletedPackageVersionsChanged}
                            label={PackageResources.FeedDetailsPane_HideDeletedPackageVersions}
                            disabled={!this.props.isUserAdmin}
                        />
                    </section>
                )}
                {!this.props.isDeleted && (
                    <section aria-labelledby={packageSharingId}>
                        <h3 id={packageSharingId} className={"fontSizeL header margin-bottom"}>
                            {PackageResources.FeedDetailsPane_PackageSharing}
                        </h3>
                        <p className={"fontSizeM margin-bottom"}>
                            {PackageResources.FeedDetailsPane_PackageBadgesDescription}
                        </p>
                        <Checkbox
                            className="edit-checkBox"
                            checked={this.props.badgesEnabled === true}
                            onChange={this._onPackageBadgesEnabledChanged}
                            label={PackageResources.FeedDetailsPane_EnablePackageBadges}
                            disabled={!this.props.isUserAdmin}
                        />
                    </section>
                )}
                {!this.props.isDeleted && (
                    <section aria-labelledby={retentionPoliciesId}>
                        <h3 id={retentionPoliciesId} className={"fontSizeL header margin-bottom"}>
                            {PackageResources.FeedDetailsPane_RetentionPolicies_Title}
                        </h3>
                        {this.props.retentionPolicyLoading ? (
                            <Spinner className="loading-spinner" size={SpinnerSize.medium} />
                        ) : (
                            <div>
                                <Checkbox
                                    className="edit-checkBox"
                                    checked={this.props.retentionPoliciesActive === true}
                                    onChange={this._onfeedRetentionActivenessChanged}
                                    label={PackageResources.FeedDetailsPane_RetentionPolicies_EnabledDescription}
                                    disabled={!this.props.isUserAdmin}
                                />

                                <p className={"fontSizeM margin-bottom"}>
                                    {PackageResources.FeedDetailsPane_RetentionPolicies_DescriptionText}
                                </p>
                                <TextField
                                    className={"text-field margin-bottom"}
                                    min={this.props.retentionPolicyMinCountLimit}
                                    max={this.props.retentionPolicyMaxCountLimit}
                                    label={PackageResources.FeedDetailsPane_RetentionPolicies_CountLimitLabel}
                                    value={
                                        this.props.retentionPolicyCountLimit !== "0"
                                            ? this.props.retentionPolicyCountLimit
                                            : ""
                                    }
                                    onChanged={(value: string) => {
                                        this._feedRetentionChange.maximumCount = value;
                                        this._onfeedRetentionPolicyChanged(this._feedRetentionChange);
                                    }}
                                    deferredValidationTime={
                                        1000
                                    } /* Default is 200.
                                            Use 1000 to have more time before error message updates */
                                    onGetErrorMessage={() => this.props.retentionPolicyCountErrorMessage}
                                    disabled={!this.props.isUserAdmin || !this.props.retentionPoliciesActive}
                                    placeholder={""}
                                />
                                <TextField
                                    className={"text-field margin-bottom"}
                                    min={this.props.retentionPolicyMinDaysToKeepLimit}
                                    max={this.props.retentionPolicyMaxDaysToKeepLimit}
                                    label={PackageResources.FeedDetailsPane_RetentionPolicies_DaysLimitLabel}
                                    value={
                                        this.props.retentionPolicyDaysLimit !== "0"
                                            ? this.props.retentionPolicyDaysLimit
                                            : ""
                                    }
                                    onChanged={(value: string) => {
                                        this._feedRetentionChange.daysToKeep = value;
                                        this._onfeedRetentionPolicyChanged(this._feedRetentionChange);
                                    }}
                                    deferredValidationTime={
                                        1000
                                    } /* Default is 200.
                                                Use 1000 to have more time before error message updates */
                                    onGetErrorMessage={() => this.props.retentionPolicyDaysToKeepErrorMessage}
                                    disabled={!this.props.isUserAdmin || !this.props.retentionPoliciesActive}
                                />
                            </div>
                        )}
                    </section>
                )}
                {this.props.isUserAdmin === true && !this.props.isDeleted && (
                    <div className="feed-details-footer">
                        <PrimaryButton
                            disabled={
                                this.props.hasChanges() === false ||
                                this.props.isSavingChanges === true ||
                                this.props.retentionPolicyCountErrorMessage.length +
                                    this.props.retentionPolicyDaysToKeepErrorMessage.length !==
                                    0
                            }
                            onClick={() => this._feedDetailsSaveChanges()}
                            text={PackageResources.FeedSettings_SaveButton_Label}
                        />
                        <DefaultButton
                            disabled={this.props.hasChanges() === false || this.props.isSavingChanges === true}
                            onClick={() => this._feedDetailsUndoChanges()}
                            text={PackageResources.FeedSettings_CancelButton_Label}
                        />
                    </div>
                )}
            </div>
        );
    }

    protected getPivotKey(): string {
        return SettingsPivotKeys.details;
    }

    private _onfeedNameChanged(value: string): void {
        FeedSettingsActionCreator.onFeedNameChanged.invoke(value);
    }

    private _onfeedDescriptionChanged(value: string): void {
        FeedSettingsActionCreator.onFeedDescriptionChanged.invoke(value);
    }

    private _onfeedRetentionActivenessChanged = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        checked: boolean
    ): void => {
        if (checked === false) {
            FeedSettingsActionCreator.onFeedRetentionPolicyChanged.invoke(null);
        } else {
            FeedSettingsActionCreator.onFeedRetentionPolicyChanged.invoke({
                maximumCount: this.props.retentionPolicyCountLimitDefault.toString(),
                daysToKeep: this.props.retentionPolicyDaysToKeepLimitDefault.toString(),
                active: true
            });
        }
    };

    private _onfeedRetentionPolicyChanged(parameters: FeedRetentionChanges): void {
        FeedSettingsActionCreator.onFeedRetentionPolicyChanged.invoke(parameters);
    }

    private _onhideDeletedPackageVersionsChanged(
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        checked: boolean
    ): void {
        FeedSettingsActionCreator.toggleHideDeletedPackageVersions.invoke(checked);
    }

    private _onPackageBadgesEnabledChanged(
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        checked: boolean
    ): void {
        FeedSettingsActionCreator.toggleBadgesEnabled.invoke(checked);
    }

    private _feedDetailsUndoChanges(): void {
        FeedSettingsActionCreator.undoFeedDetailsChanges.invoke(null);
    }

    private _feedDetailsSaveChanges(): void {
        FeedSettingsActionCreator.saveFeedDetailsChanges.invoke(null);
    }
    private _feedDetailsUpgrade(): void {
        FeedSettingsActionCreator.upgradeFeed.invoke(null);
        this.setState({ upgradeButtonClicked: true });
    }
}

function mapStateToProps(state: IFeedHubViewAwareState): IFeedDetailsPaneProps2 {
    return {
        isDeleted: isSelectedFeedDeleted(state)
    };
}

export const FeedDetailsPane = connect(mapStateToProps)(UnconnectedFeedDetailsPane);
